import { router, usePage } from "@inertiajs/react";
import { AlertDialog, Box, Button, Callout, Flex } from "@radix-ui/themes";
import { useState } from "react";

import type { SharedProps } from "@/lib/types";

export function TimeTravelBanner() {
  const page = usePage<SharedProps>();
  const appClock = page.props.app_clock;
  const showControls = Boolean(page.props.development_features?.show_time_travel);
  const [sandboxConfirmOpen, setSandboxConfirmOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  if (!appClock?.enabled || appClock.offset_weeks <= 0) return null;

  function postAction(path: string, label: string) {
    setBusy(label);
    router.post(path, {}, { preserveScroll: true, onFinish: () => setBusy(null) });
  }

  return (
    <Box px="4" py="2" className="border-b bg-amber-2" data-testid="time-travel-banner">
      <Callout.Root color="amber" size="1">
        <Flex direction="column" gap="2" className="sm:flex-row sm:items-center sm:justify-between">
          <Callout.Text>
            Environment app clock +{appClock.offset_weeks} week{appClock.offset_weeks === 1 ? "" : "s"} — today is{" "}
            {appClock.today_iso8601} (week of {appClock.current_week_start_on}) for all users on this host.
          </Callout.Text>
          {showControls ? (
            <Flex gap="2" wrap="wrap">
              <Button
                size="1"
                variant="soft"
                disabled={busy !== null}
                onClick={() => postAction("/admin/time_travel/advance", "advance")}
              >
                {busy === "advance" ? "Advancing…" : "Advance +1 week"}
              </Button>
              {appClock.sandbox_reset_enabled ? (
                <Button
                  size="1"
                  color="red"
                  variant="soft"
                  disabled={busy !== null}
                  onClick={() => setSandboxConfirmOpen(true)}
                >
                  Reset sandbox…
                </Button>
              ) : null}
            </Flex>
          ) : null}
        </Flex>
      </Callout.Root>

      <AlertDialog.Root open={sandboxConfirmOpen} onOpenChange={setSandboxConfirmOpen}>
        <AlertDialog.Content maxWidth="520px">
          <AlertDialog.Title>Reset sandbox to baseline?</AlertDialog.Title>
          <AlertDialog.Description size="2">
            This deletes all application data (projects, planner rows, users except seeds), clears saved mock
            scenarios, restores MockServer baseline expectations, and resets the app clock. Everyone on this host
            is affected. You will need to sign in again.
          </AlertDialog.Description>
          <Flex justify="end" gap="2" mt="4">
            <AlertDialog.Cancel>
              <Button variant="soft">Cancel</Button>
            </AlertDialog.Cancel>
            <AlertDialog.Action>
              <Button
                color="red"
                onClick={() => {
                  setSandboxConfirmOpen(false);
                  setBusy("reset_sandbox");
                  router.post("/admin/time_travel/reset_sandbox");
                }}
              >
                Reset sandbox
              </Button>
            </AlertDialog.Action>
          </Flex>
        </AlertDialog.Content>
      </AlertDialog.Root>
    </Box>
  );
}
